import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { presentToastError } from "../shared/Toast";
import AutomationService from "../../services/automation.service";

export default function useAutomationFlows(hotelId) {
  const { t } = useTranslation();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const currentHotel = useRef(hotelId);
  const sequence = useRef(0);
  currentHotel.current = hotelId;
  const explain = useCallback((error, action) => {
    const code = error.response?.data?.code;
    if (action === "duplicate" && code === "FLOW_VALIDATION" &&
        /^action: Invalid discriminator value/.test(error.response?.data?.message || "")) {
      return t("automation.errors.duplicateUnavailable");
    }
    if (action === "duplicate" && (!code || ["FLOW_VALIDATION", "FLOW_ERROR"].includes(code))) {
      return t("automation.errors.duplicateFailed");
    }
    return t(`automation.errors.${code}`, { defaultValue: t("automation.errors.default") });
  }, [t]);
  const reload = useCallback(async () => {
    const request = ++sequence.current;
    try {
      const result = await AutomationService.list(hotelId);
      if (currentHotel.current === hotelId && request === sequence.current) {
        setState(result.data);
        setError("");
      }
    } catch (error) {
      if (currentHotel.current === hotelId && request === sequence.current) {
        const message = explain(error);
        setError(message);
        presentToastError(message);
      }
    }
  }, [hotelId, explain]);
  useEffect(() => {
    setState(null);
    setError("");
    setBusy(false);
    if (hotelId) reload();
    return () => { sequence.current += 1; };
  }, [hotelId, reload]);

  const change = async command => {
    setBusy(true);
    setError("");
    sequence.current += 1;
    try {
      const result = await AutomationService.change(hotelId, command);
      if (currentHotel.current !== hotelId) return null;
      setState(result.data);
      return result.data;
    } catch (error) {
      if (currentHotel.current === hotelId) {
        // Refresh the slots after a conflict, but keep the user's editor mounted.
        try {
          const result = await AutomationService.list(hotelId);
          if (currentHotel.current === hotelId) setState(result.data);
        } catch (refreshError) {
          setError(explain(refreshError));
        }
        if (currentHotel.current === hotelId) {
          const message = explain(error, command.action);
          setError(message);
          presentToastError(message);
        }
      }
      return null;
    } finally {
      if (currentHotel.current === hotelId) setBusy(false);
    }
  };
  return { state, busy, error, change, reload };
}
